/**
 * pic.hide_atc — gift products cannot be added or quantity-increased.
 * Also: reliable gift Remove, and purge orphan gifts when no paid items remain.
 */
(() => {
  if (window.__piHideAtcReady) return;
  window.__piHideAtcReady = true;

  const root = () => window.Shopify?.routes?.root || '/';
  let purgeTimer = null;
  let purging = false;

  const readConfig = () => {
    const el = document.getElementById('pi-hide-atc-cart-gifts');
    if (!el) return { variantIds: [], lineKeys: [], productVariantIds: [] };
    try {
      return JSON.parse(el.textContent) || {};
    } catch (error) {
      return { variantIds: [], lineKeys: [], productVariantIds: [] };
    }
  };

  const toSet = (list) => new Set((list || []).map((v) => String(v)));

  const isGiftVariantId = (id) => {
    const cfg = readConfig();
    const idStr = String(id || '');
    return toSet(cfg.variantIds).has(idStr) || toSet(cfg.productVariantIds).has(idStr);
  };

  const isGiftLineKey = (key) => toSet(readConfig().lineKeys).has(String(key || ''));

  const giftFormFromTarget = (target) => {
    const form = target?.closest?.(
      'form[data-instant-form-line-item-key], form.i6wjcaUafpoBJq6M6, form[data-pi-gift]'
    );
    if (!form) return null;
    const key = form.getAttribute('data-instant-form-line-item-key');
    const variantId = form.getAttribute('data-instant-form-variant-id');
    if (
      form.classList.contains('pi-cart-line--gift') ||
      form.hasAttribute('data-pi-gift') ||
      isGiftLineKey(key) ||
      isGiftVariantId(variantId)
    ) {
      return form;
    }
    return null;
  };

  const collectGiftVariantIds = () => {
    const ids = toSet(readConfig().variantIds);
    document
      .querySelectorAll(
        '.__instant.iyU2woGEZqwFyagfI form[data-pi-gift], .__instant.iyU2woGEZqwFyagfI form.pi-cart-line--gift'
      )
      .forEach((form) => {
        const id = form.getAttribute('data-instant-form-variant-id');
        if (id) ids.add(String(id));
      });
    return ids;
  };

  const notifyCart = async () => {
    try {
      const response = await fetch(`${root()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { cart, baseEvent: 'pi-gift-sync' },
        })
      );
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  };

  const removeLineByKey = async (key) => {
    const response = await fetch(`${root()}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
    if (!response.ok) {
      const json = await response.json().catch(() => ({}));
      throw new Error(json.description || json.message || 'Unable to remove gift');
    }
    return response.json();
  };

  const purgeOrphanGifts = async () => {
    if (purging) return;
    purging = true;
    try {
      const response = await fetch(`${root()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      const items = cart.items || [];
      if (!items.length) return;

      const giftIds = collectGiftVariantIds();
      if (!giftIds.size) return;

      const nonGift = items.filter((item) => !giftIds.has(String(item.variant_id)));
      if (nonGift.length > 0) return;

      const updates = {};
      items.forEach((item) => {
        if (giftIds.has(String(item.variant_id))) updates[item.key] = 0;
      });
      if (!Object.keys(updates).length) return;

      const updateRes = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ updates }),
      });
      if (!updateRes.ok) return;
      await notifyCart();
    } catch (error) {
      console.warn('[pi-hide-atc] orphan gift purge failed', error);
    } finally {
      purging = false;
    }
  };

  const schedulePurge = () => {
    clearTimeout(purgeTimer);
    purgeTimer = setTimeout(purgeOrphanGifts, 250);
  };

  // Block Instant quantity-increment on gift lines
  document.addEventListener(
    'click',
    (event) => {
      const increment = event.target?.closest?.(
        '[data-instant-action-type="quantity-increment"], .iy0l8OUHfKV2UWT4I'
      );
      if (!increment) return;
      const giftForm = giftFormFromTarget(increment);
      if (!giftForm) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    },
    true
  );

  // Force gift Remove via cart/change.js (Instant often fails when min qty was locked)
  document.addEventListener(
    'click',
    (event) => {
      const removeBtn = event.target?.closest?.(
        '[data-pi-gift-remove], [data-instant-action-type="adjust-line-item-quantity"]'
      );
      if (!removeBtn) return;
      const giftForm = giftFormFromTarget(removeBtn);
      if (!giftForm) return;

      const key =
        removeBtn.getAttribute('data-instant-action-line-item-key') ||
        giftForm.getAttribute('data-instant-form-line-item-key');
      if (!key) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      if (removeBtn.getAttribute('aria-busy') === 'true') return;
      removeBtn.setAttribute('aria-busy', 'true');

      removeLineByKey(key)
        .then(() => notifyCart())
        .then(() => schedulePurge())
        .catch((error) => console.warn('[pi-hide-atc] gift remove failed', error))
        .finally(() => removeBtn.removeAttribute('aria-busy'));
    },
    true
  );

  // Block Instant ATC / redirect-to-cart on gift PDPs
  document.addEventListener(
    'click',
    (event) => {
      const atc = event.target?.closest?.(
        '[data-instant-action-type="add-to-instant-cart"], [data-pi-atc], .pi-sticky-atc__btn, a[data-instant-action-type="redirect-to-cart"]'
      );
      if (!atc) return;
      const variantId =
        atc.getAttribute('data-instant-action-variant-id') ||
        atc.getAttribute('data-variant-id') ||
        atc.closest('[data-instant-form-variant-id]')?.getAttribute('data-instant-form-variant-id');
      if (!isGiftVariantId(variantId)) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    },
    true
  );

  // Block fetch cart/add for gift variant IDs on gift PDPs
  const originalFetch = window.fetch?.bind(window);
  if (originalFetch) {
    window.fetch = async function (input, init) {
      try {
        const url = typeof input === 'string' ? input : input?.url || '';
        if (/\/cart\/(add|change)(\.js)?/i.test(url) && init?.body) {
          let body = init.body;
          if (typeof body === 'string') {
            try {
              body = JSON.parse(body);
            } catch (error) {
              body = null;
            }
          }
          if (body) {
            const items = Array.isArray(body.items) ? body.items : body.id ? [body] : [];
            for (const item of items) {
              const id = item?.id || item?.variant_id;
              if (isGiftVariantId(id)) {
                const cfg = readConfig();
                const isGiftPdp = (cfg.productVariantIds || []).length > 0;
                if (/\/cart\/add/i.test(url) && isGiftPdp) {
                  return new Response(JSON.stringify({ description: 'Gift product cannot be added' }), {
                    status: 422,
                    headers: { 'Content-Type': 'application/json' },
                  });
                }
                if (/\/cart\/change/i.test(url)) {
                  const key = body.line || body.id || item.key;
                  const nextQty = Number(body.quantity);
                  if (isGiftLineKey(key) || isGiftVariantId(id)) {
                    const form = document.querySelector(
                      `form[data-instant-form-line-item-key="${CSS.escape(String(key))}"]`
                    );
                    const current =
                      Number(form?.querySelector('input[name="quantity"]')?.value) ||
                      Number(document.querySelector(`[data-line-key="${CSS.escape(String(key))}"]`)?.value) ||
                      1;
                    if (!Number.isNaN(nextQty) && nextQty > current) {
                      return new Response(JSON.stringify({ description: 'Gift quantity cannot be increased' }), {
                        status: 422,
                        headers: { 'Content-Type': 'application/json' },
                      });
                    }
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        // fall through to original fetch
      }
      const result = originalFetch(input, init);
      try {
        const url = typeof input === 'string' ? input : input?.url || '';
        if (/\/cart\/(add|change|update|clear)(\.js)?/i.test(url)) {
          Promise.resolve(result).then(() => schedulePurge());
        }
      } catch (error) {}
      return result;
    };
  }

  // Theme quantity-input: cap gift lines at current qty
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target?.closest?.('input.quantity-input, input[name="quantity"]');
      if (!input) return;
      const key = input.getAttribute('data-line-key');
      const form = input.closest('form[data-instant-form-line-item-key]');
      const formKey = form?.getAttribute('data-instant-form-line-item-key');
      const variantId = form?.getAttribute('data-instant-form-variant-id');
      if (!isGiftLineKey(key) && !isGiftLineKey(formKey) && !isGiftVariantId(variantId)) return;

      const max = Number(input.getAttribute('data-pi-gift-max') || input.value || 1);
      if (Number(input.value) > max) {
        input.value = String(max);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    },
    true
  );

  document.addEventListener('cart:change', schedulePurge);
  document.addEventListener('cart:refresh', schedulePurge);
  document.documentElement.addEventListener('cart:change', schedulePurge);
  document.documentElement.addEventListener('cart:refresh', schedulePurge);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedulePurge);
  } else {
    schedulePurge();
  }
})();
